import React, { useState, useMemo, useEffect } from 'react';
import { Calendar, Badge, Tabs, Modal, Form, Spin, message, Select, Table } from 'antd';
import { Header } from '../../../../components';
import { UseFetchSchedule, UseFetchCreateSchedule, UseFetchUserSchedule } from '../hooks/useFetch';
import { UseFetchListEnum } from '../../../../hooks/useFetchEnums';
import { HOLIDAY_TYPE_OPTIONS } from '../../../../data/constant';
import moment from 'moment';
import dayjs from 'dayjs';
import { ENUM_TYPE } from '../../../../data/constant'

const { Option } = Select;

function SchedulePage() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [activeTab, setActiveTab] = useState('day');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectMonthDay, setSelectMonthDay] = useState(dayjs().format('YYYY-MM'));
  const [selectMonthNight, setSelectMonthNight] = useState(dayjs().format('YYYY-MM'));
  const [selectMonthMix, setSelectMonthMix] = useState(dayjs().format('YYYY-MM'));
  const [selectMonth, setSelectMonth] = useState(dayjs().format('YYYY-MM'));
  const { mutate: createSchedule, isLoading: isCreating } = UseFetchCreateSchedule();
  const [form] = Form.useForm();
  const { data: allHolidays = [], isLoading, refetch: refetchSchedule } = UseFetchSchedule(selectMonth);
  const { data: employees = [], isLoading: isLoadingEmployees, refetch: refetchEmployees } = UseFetchUserSchedule(activeTab);
  const [holidayTypeOptions, setHolidayTypeOptions] = useState([]);
  const { mutateAsync: listEnum } = UseFetchListEnum();

  // holidays คือ array ที่ได้จาก API (วันหยุดของพนักงาน)
  const calendarData = useMemo(() => {
    const map = {};
    allHolidays.forEach(item => {
      const dateStr = moment(item.date).format('YYYY-MM-DD');
      // ตรวจสอบ shift ให้ตรงกับ activeTab (2 = day, 3 = night, 4 = afternoon)
      let shiftValue;
      if (activeTab === 'day') shiftValue = '2';
      else if (activeTab === 'night') shiftValue = '3';
      else if (activeTab === 'afternoon') shiftValue = '4';

      if (String(item.shift) === shiftValue) {
        if (!map[dateStr]) map[dateStr] = [];
        map[dateStr].push(item);
      }
    });
    return map;
  }, [allHolidays, activeTab]);


  useEffect(() => {
    listEnumHolidayType()
  }, []);
  useEffect(() => {
    if (activeTab === 'day') {
      setSelectMonth(selectMonthDay);
    } else if (activeTab === 'night') {
      setSelectMonth(selectMonthNight);
    } else if (activeTab === 'afternoon') {
      setSelectMonth(selectMonthMix);
    }
  }, [activeTab, selectMonthDay, selectMonthNight, selectMonthMix]);

  useEffect(() => {
    refetchSchedule();
  }, [selectMonth]);

  const listEnumHolidayType = async () => {
    try {
      const result = await listEnum({ enumType: ENUM_TYPE.holiday_type });
      setHolidayTypeOptions(result);
      return result;
    } catch (error) {
      await message.destroy();
      message.error("Failed To List Enum.. ");
    }
  };
  // แสดงข้อมูลใน cell ของปฏิทิน (แสดงรายชื่อคนที่หยุด)
  const dateCellRender = (value) => {
    const dateStr = value.format('YYYY-MM-DD');
    const items = calendarData[dateStr] || [];
    return (
      <div style={{ minHeight: 40 }}>
        {items.slice(0, 3).map((item) => (
          <div key={item.id}>
            <Badge
              status="error"
              text={`${item.UserName}`}
            />
          </div>
        ))}
        {items.length > 3 && (
          <div style={{ fontSize: '12px', color: '#999' }}>
            +{items.length - 3} คน
          </div>
        )}
      </div>
    );
  };

  const cellRender = (current, info) => {
    if (info.type === 'date') return dateCellRender(current);
    return info.originNode;
  };

  // เมื่อเลือกวันที่
  const handleDateSelect = (value) => {
    setSelectedDate(value);
    form.resetFields();
    setSelectedEmployees([]);

    // หาข้อมูลที่มีอยู่แล้วในวันที่เลือก (คนที่หยุดอยู่แล้ว)
    const dateStr = value.format('YYYY-MM-DD');
    const existingHolidays = calendarData[dateStr] || [];

    // สร้าง initial selected employees พร้อม holidayType
    const initialSelected = existingHolidays.map(item => ({
      employeeId: item.employeeId,
      UserName: item.UserName,
      holidayType: item.holidayType ? parseInt(item.holidayType) : null
    }));

    setSelectedEmployees(initialSelected);
    setIsModalOpen(true);
  };

  // เมื่อเลือกพนักงานจาก Select
  const handleEmployeeSelect = (employeeIds) => {
    const newSelected = employeeIds.map(empId => {
      const existing = selectedEmployees.find(e => e.employeeId === empId);
      if (existing) return existing;

      const emp = employees.find(e => e.id === empId);
      return {
        employeeId: empId,
        UserName: emp?.UserName || '',
        //holidayType: 'dayoff'
      };
    });
    setSelectedEmployees(newSelected);
  };

  // เมื่อเปลี่ยนประเภทวันหยุดของพนักงาน
  const handleHolidayTypeChange = (employeeId, holidayType) => {
    setSelectedEmployees(prev =>
      prev.map(emp =>
        emp.employeeId === employeeId
          ? { ...emp, holidayType }
          : emp
      )
    );
  };

  // เมื่อเปลี่ยน tab (shift)
  const onTabChange = (key) => {
    setActiveTab(key);
    setSelectedDate(null);
    refetchEmployees();
  };

  // เมื่อกด OK ใน Modal
  const handleModalOk = async () => {
    try {
      setModalLoading(true);
      const date = selectedDate.format('YYYY-MM-DD');
      const shift = activeTab; // 'day' หรือ 'night'

      // สร้าง payload สำหรับบันทึกวันหยุด
      const holidays = selectedEmployees.map((emp) => ({
        employeeId: Number(emp.employeeId),
        date,
        shift,
        holidayType: emp.holidayType,
      }));

      if (holidays.length > 0) {
        await createSchedule(holidays);
        message.success('บันทึกวันหยุดสำเร็จ');
      } else {
        message.info('ไม่มีพนักงานที่เลือก');
      }

      setTimeout(() => {
        refetchSchedule();
      }, 500);

      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
      message.error('กรุณาตรวจสอบข้อมูล');
    } finally {
      setModalLoading(false);
    }
  };

  // เมื่อกด Cancel ใน Modal
  const handleModalCancel = () => {
    setIsModalOpen(false);
  };

  // Columns สำหรับ Table แสดงพนักงานที่เลือก
  const tableColumns = [
    {
      title: 'พนักงาน',
      dataIndex: 'UserName',
      key: 'UserName',
    },
    {
      title: 'ประเภทวันหยุด',
      dataIndex: 'holidayType',
      key: 'holidayType',
      render: (holidayType, record) => (
        <Select
          value={holidayType}
          style={{ width: '100%' }}
          onChange={(value) => handleHolidayTypeChange(record.employeeId, value)}
          options={holidayTypeOptions}
        />
      ),
    },
  ];

  // Tabs สำหรับ Day/Night
  const tabItems = [
    {
      key: 'day',
      label: 'เช้า (Day)',
      children: (
        isLoading ? <Spin /> :
          <Calendar
            onSelect={handleDateSelect}
            cellRender={cellRender}
            onPanelChange={(value) => {
              setSelectMonthDay(dayjs(value).format('YYYY-MM'));
            }}
          />
      ),
    },
    {
      key: 'night',
      label: 'คํ่า(Night)',
      children: (
        isLoading ? <Spin /> :
          <Calendar
            onSelect={handleDateSelect}
            cellRender={cellRender}
            onPanelChange={(value) => {
              setSelectMonthNight(dayjs(value).format('YYYY-MM'));
            }}
          />
      ),
    },
    {
      key: 'afternoon',
      label: 'กลาง (Mix)',
      children: (
        isLoading ? <Spin /> :
          <Calendar
            onSelect={handleDateSelect}
            cellRender={cellRender}
            onPanelChange={(value) => {
              setSelectMonthMix(dayjs(value).format('YYYY-MM'));
            }}
          />
      ),
    },
  ];

  return (
    <div className="md:m-10">
      <div className="flex justify-between mb-6">
        <Header category="App" title="จัดการวันหยุด" />
      </div>
      <Tabs defaultActiveKey="day" items={tabItems} onChange={onTabChange} />
      <Modal
        title={`จัดการวันหยุด - ${selectedDate?.format('DD/MM/YYYY')} (${activeTab === 'day' ? 'กะวัน' : activeTab === 'night' ? 'กะคืน' : 'กะบ่าย'})`}
        open={isModalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={modalLoading}
        destroyOnClose
        width={700}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
            เลือกพนักงานที่จะหยุด:
          </label>
          <Select
            mode="multiple"
            placeholder="เลือกพนักงาน"
            loading={isLoadingEmployees}
            style={{ width: '100%' }}
            optionFilterProp="children"
            showSearch
            value={selectedEmployees.map(e => e.employeeId)}
            onChange={handleEmployeeSelect}
          >
            {employees.map(emp => (
              <Option key={emp.id} value={emp.id}>
                {emp.UserName}
              </Option>
            ))}
          </Select>
        </div>

        {selectedEmployees.length > 0 && (
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              กำหนดประเภทวันหยุด:
            </label>
            <Table
              dataSource={selectedEmployees}
              columns={tableColumns}
              rowKey="employeeId"
              pagination={false}
              size="small"
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

export default SchedulePage;
